import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Inventory = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ warehouse_id: '', search: '' });
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchWarehouses();
    fetchItems();
  }, [filters]);

  const fetchWarehouses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch warehouses');
      const data = await response.json();
      setWarehouses(data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
      if (filters.search) params.append('product_name', filters.search);

      const response = await fetch(`${API_URL}/api/inventory/items?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch items');
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load inventory',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (item) => {
    const available = item.quantity_available || 0;
    if (available <= 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (available < 10) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Inventory</h2>
            <p className="text-sm text-[#6C757D]">Manage stock levels across warehouses</p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => navigate('/admin/procurement/inventory/warehouses')}
              variant="outline"
            >
              Manage Warehouses
            </Button>
            <Button
              onClick={() => navigate('/admin/procurement/inventory/movements')}
              variant="outline"
            >
              View Movements
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6C757D]" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
            </div>
            <div>
              <select
                value={filters.warehouse_id}
                onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              >
                <option value="">All Warehouses</option>
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading inventory...</p>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6C757D] mb-4">No inventory items found</p>
              <p className="text-sm text-[#6C757D]">Items will appear here when Purchase Orders are received</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((item) => {
            const status = getStockStatus(item);
            return (
              <Card
                key={item.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/admin/procurement/inventory/${item.id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Package className="h-5 w-5 text-[#00B4D8]" />
                        <h3 className="text-lg font-semibold text-[#2B2D42]">{item.product_name}</h3>
                        <Badge className={status.color}>{status.label}</Badge>
                      </div>
                      {item.sku && (
                        <p className="text-sm text-[#6C757D] mb-2">SKU: {item.sku}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-[#6C757D]">
                        <span>Warehouse: {item.warehouse_name}</span>
                        <span>•</span>
                        <span>Unit: {item.unit}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#6C757D] mb-1">Available</p>
                      <p className="text-3xl font-bold text-[#00B4D8]">
                        {item.quantity_available?.toFixed(0) || 0}
                      </p>
                      {item.quantity_reserved > 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Reserved: {item.quantity_reserved}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Inventory;